import { Fragment } from 'react';
import { useFormContext } from 'react-hook-form';
// Hooks
import useToggle from 'hooks/useToggle';
// Redux
import { useAppSelector } from 'hooks/useStore';
import { selectQuiz } from 'store/quiz/quizzesSelectors';
import PageTypes from 'types/PageTypes';
import QuizTypes from 'types/QuizTypes';
import IPage from 'models/Page';
// Components
import Question from './Question';
import Banner from './Banner';
import Timer from './Timer';
import Waiting from './Waiting';
import AdditionalQuestions from './AdditionalQuestions';
import Waiver from './SystemPages/Waiver';
import LandingPage from './SystemPages/LandingPage';
import Registration from './SystemPages/Registration';
import CallToAction from './SystemPages/CallToAction';
import Results from './SystemPages/Results';
import CountdownPage from './SystemPages/Countdown';
import PromoVideo from './SystemPages/PromoVideo';
import BrandVideo from './SystemPages/BrandVideo';
import ScoringResult from './SystemPages/ScoringResult';
import { Button } from 'components/Buttons';
// Icons
import {
  MdOutlineKeyboardDoubleArrowDown,
  MdOutlineKeyboardDoubleArrowUp
} from "react-icons/md";
// i18next
import { useTranslation } from 'react-i18next';

const TabContentPages:React.FC = () => {
  const { t } = useTranslation();

  const quiz = useAppSelector(selectQuiz);

  const { watch } = useFormContext();

  const watchedPages = watch('pages') || [];
  const watchedRegistrationPage = watch('registrationPage');

  const isSurveyQuiz = quiz?.type === QuizTypes.Survey;
  const isPersonalityQuiz = quiz?.type === QuizTypes.Personality;

  // const PagesAccordion:React.FC<any> = ({ label, children }) => (
  //   <MuiAccordion
  //     aria-controls={`${label}-content`}
  //     id={`${label}-header`}
  //     className={classes.accordion}
  //       style={{
  //       background: 'none',
  //       boxShadow: 'none',
  //       marginBottom: '10px',
  //     }}
  //   >
  //     <AccordionSummary
  //       expandIcon={<MdExpandMore color="white" />}
  //       style={{
  //         padding: '16px',
  //         color: 'rgba(255,255,255,0.6)', fontSize: '14px', fontWeight: 500, letterSpacing: '1.5px',
  //       }}
  //     >{label}</AccordionSummary>
  //     <AccordionDetails style={{ padding: '0' }}>
  //       {children}
  //     </AccordionDetails>
  //   </MuiAccordion>
  // )

  const { open:preGamesSectionsExpanded, toggle:togglePreGamesSections } = useToggle();
  const { open:gamesSectionsExpanded, toggle:toggleGamesSections } = useToggle();
  const { open:postGamesSectionsExpanded, toggle:togglePostGamesSections } = useToggle();

  return (
    <div className="w-full overflow-y-auto overflow-x-hidden">
      <div className="flex justify-between items-center gap-2 mb-2">
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', fontWeight: 500, letterSpacing: '1.5px', paddingLeft: '10px', textTransform: 'uppercase' }}>
          {t('pages.quizForm.preGameScreens')}
        </p>
        <Button size="sm" className="flex items-center gap-1" onClick={togglePreGamesSections}>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>
            {preGamesSectionsExpanded ? t('buttons.collapseAll') : t('buttons.expandAll')}
          </span>
          {preGamesSectionsExpanded ? <MdOutlineKeyboardDoubleArrowUp /> : <MdOutlineKeyboardDoubleArrowDown />}
        </Button>
      </div>
      <PromoVideo defaultExpanded={preGamesSectionsExpanded} />
      <LandingPage defaultExpanded={preGamesSectionsExpanded} />
      <Waiver defaultExpanded={preGamesSectionsExpanded} />

      {!isSurveyQuiz ? <BrandVideo defaultExpanded={preGamesSectionsExpanded} /> : null}
      
      {watchedRegistrationPage?.showPriorQuiz && (
        <Registration defaultExpanded={preGamesSectionsExpanded} />
      )}

      {!isSurveyQuiz ? <CountdownPage defaultExpanded={preGamesSectionsExpanded} /> : null}

      <div className="flex justify-between items-center gap-2 mb-2">
        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', fontWeight: 500, letterSpacing: '1.5px', paddingLeft: '10px', textTransform: 'uppercase' }}>
          {t('pages.quizForm.gameScreen')}
        </p>
        <Button size="sm" className="flex items-center gap-1" onClick={toggleGamesSections}>
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>
            {gamesSectionsExpanded ? t('buttons.collapseAll') : t('buttons.expandAll')}
          </span>
          {gamesSectionsExpanded ? <MdOutlineKeyboardDoubleArrowUp /> : <MdOutlineKeyboardDoubleArrowDown />}
        </Button>
      </div>
      {watchedPages.map((page:IPage) => (
        <div key={`${page.type}-item-${page.uuid}`} react-scroll={`${page.type}-${page.uuid}`}>
          {page.type === PageTypes.Question && page.refs.map(ref => (
            <Question
              key={ref}
              questionRef={ref}
              defaultExpanded={gamesSectionsExpanded}
            />
          ))}
          {page.type === PageTypes.AdditionalQuestions && (
            <AdditionalQuestions page={page} defaultExpanded={gamesSectionsExpanded} />
          )}
          {page.type === PageTypes.Banner && page.refs.map(ref => (
            <Banner key={ref} bannerRef={ref} defaultExpanded={gamesSectionsExpanded} />
          ))}
          {page.type === PageTypes.Timer && page.refs.map(ref => (
            <Timer key={ref} timerRef={ref} defaultExpanded={gamesSectionsExpanded} />
          ))}
          {page.type === PageTypes.Waiting && page.refs.map(ref => (
            <Waiting key={ref} waitingRef={ref} defaultExpanded={gamesSectionsExpanded} />
          ))}
        </div>
      ))}
      {!isPersonalityQuiz || !watchedRegistrationPage?.showPriorQuiz ? (
        <Fragment>
          <div className="flex justify-between items-center gap-2 mb-2">
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', fontWeight: 500, letterSpacing: '1.5px', paddingLeft: '10px', textTransform: 'uppercase' }}>
              {t('pages.quizForm.postGameScreens')}
            </p>
            <Button size="sm" className="flex items-center gap-1" onClick={togglePostGamesSections}>
              <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                {postGamesSectionsExpanded ? t('buttons.collapseAll') : t('buttons.expandAll')}
              </span>
              {postGamesSectionsExpanded ? <MdOutlineKeyboardDoubleArrowUp /> : <MdOutlineKeyboardDoubleArrowDown />}
            </Button>
          </div>
          <ScoringResult defaultExpanded={postGamesSectionsExpanded} />
          {!watchedRegistrationPage?.showPriorQuiz && (
            <Registration defaultExpanded={postGamesSectionsExpanded} />
          )}
          <CallToAction defaultExpanded={postGamesSectionsExpanded} />
          <Results defaultExpanded={postGamesSectionsExpanded} />
        </Fragment>
      ) : null}
    </div>
  )
}

export default TabContentPages;

// const useStyles = makeStyles({
//   accordion: {
//     borderBottom: "1px solid rgba(255,255,255,0.2)",
//     "&.Mui-expanded": {
//       borderBottom: "none",
//     },
//   },
// })